import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Square, Volume2, VolumeX, ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { usePlayer, PLAYABLE } from "../../context/PlayerContext";
import { useAuth } from "../../context/AuthContext";
import { track as trackEvent } from "../../lib/analytics";

export const DIM_ROUTES = { "01": "/dimension-i", "02": "/dimension-ii", "03": "/dimension-iii", "04": "/dimension-iv" };
const fmt = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;
const EASE = [0.16, 1, 0.3, 1];

function HeroCard({ slide, off, reduced, onSelect, onEnter }) {
  if (Math.abs(off) > 2) return null;
  const conf = {
    0: { shift: 0, scale: 1, opacity: 1, z: 30 },
    1: { shift: 58, scale: 0.78, opacity: 0.45, z: 20 },
    2: { shift: 100, scale: 0.6, opacity: 0.18, z: 10 },
  }[Math.abs(off)];
  const shift = off < 0 ? -conf.shift : conf.shift;
  const isCenter = off === 0;

  return (
    <motion.div
      className="absolute left-1/2 top-1/2 w-[66vw] max-w-[300px] md:max-w-[420px] aspect-square"
      style={{ zIndex: conf.z }}
      initial={false}
      animate={{ x: `${shift - 50}%`, y: "-50%", scale: conf.scale, opacity: conf.opacity }}
      transition={{ duration: reduced ? 0 : 0.9, ease: EASE }}
      data-testid={`hero-card-${slide.number}`}
    >
      <button
        onClick={() => (isCenter ? onEnter(slide) : onSelect())}
        aria-label={isCenter ? `Enter ${slide.title}` : `Select ${slide.title}`}
        aria-current={isCenter || undefined}
        data-testid={`hero-card-btn-${slide.number}`}
        className="group block w-full h-full rounded-[20px] overflow-hidden relative ring-1 ring-white/10 focus-visible:ring-2 focus-visible:ring-gold outline-none shadow-[0_30px_80px_rgba(0,0,0,0.65)]"
      >
        {slide.restricted ? (
          <div className="w-full h-full bg-[#030303] relative flex flex-col items-center justify-center">
            <div className="signal-static" />
            <span className="relative font-serif text-7xl md:text-8xl text-white/20">05</span>
            <span className="relative font-mono text-[9px] md:text-[10px] tracking-[0.4em] text-red-500/80 mt-4">ACCESS RESTRICTED</span>
          </div>
        ) : slide.artwork ? (
          <img src={slide.artwork} alt={`${slide.title} — ${slide.identity || slide.theme}`}
            className="w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-[1.04]" />
        ) : (
          <div className="w-full h-full signal-veil bg-[#08090c] relative flex flex-col items-center justify-center">
            <span className="font-serif text-7xl md:text-8xl text-white/15">{slide.number}</span>
            <span className="font-mono text-[9px] tracking-[0.35em] text-white/35 mt-4">{slide.title}</span>
            <span className="font-mono text-[8px] tracking-[0.35em] text-gold/60 mt-2">{slide.status}</span>
          </div>
        )}
        <div className="absolute inset-0 rounded-[20px] ring-1 ring-inset ring-white/15 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white/[0.07] to-transparent pointer-events-none" />
      </button>
    </motion.div>
  );
}

function SlideDetails({ slide, user, onEnter }) {
  const navigate = useNavigate();
  const { playTrack, current, playing, progress, duration, toggle, stop, muted, toggleMute, seek } = usePlayer();
  const t = slide.featuredTrack;
  const active = t && current && current.releaseSlug === slide.releaseSlug && current.trackNumber === t.trackNumber;
  const playable = t && PLAYABLE.includes(t.availability);
  const entitled = user && user.dimensionV === "active";

  const ctaPrimary = slide.restricted
    ? (entitled ? { label: "ENTER DIMENSION V", target: "/dimension-v" } : { label: "LEARN HOW TO UNLOCK", target: "/complete-set" })
    : slide.ctaPrimary || { label: `ENTER ${slide.title}`, target: DIM_ROUTES[slide.number] || "/" };
  const ctaSecondary = slide.restricted
    ? (entitled ? null : { label: "REDEEM ACCESS KEY", target: "/dimension-v/access" })
    : slide.ctaSecondary || (slide.releaseSlug ? { label: "EXPLORE ALBUM", target: `/music/${slide.releaseSlug}` } : null);

  return (
    <div data-testid={`hero-details-${slide.number}`}>
      {slide.description && (
        <p className="text-white/55 text-sm md:text-base leading-relaxed max-w-md">{slide.description}</p>
      )}

      {/* Featured transmission / player */}
      {!slide.restricted && (
        <div className="mt-8 border border-white/10 bg-white/[0.02] backdrop-blur-sm rounded-[14px] p-5 max-w-md" data-testid={`hero-player-${slide.number}`}>
          <p className="font-mono text-[9px] tracking-[0.35em] text-gold/80 mb-3">
            {active && playing ? "NOW PLAYING" : "FEATURED TRANSMISSION"}
          </p>
          {t ? (
            <>
              <div className="flex items-center gap-4">
                {playable ? (
                  <button
                    onClick={() => (active ? toggle() : playTrack(t, {
                      releaseSlug: slide.releaseSlug, releaseTitle: slide.releaseTitle,
                      artwork: slide.artwork, dimension: slide.number, source: "hero" }))}
                    data-testid="hero-play-btn"
                    aria-label={active && playing ? `Pause ${t.title}` : `Play ${t.title}`}
                    className="w-11 h-11 rounded-full bg-gold text-black flex items-center justify-center hover:bg-white transition-colors shrink-0"
                  >
                    {active && playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
                  </button>
                ) : (
                  <span className="w-11 h-11 rounded-full border border-white/15 text-white/30 flex items-center justify-center shrink-0" aria-hidden="true">
                    <Lock size={14} />
                  </span>
                )}
                <div className="min-w-0">
                  <p className="font-serif text-xl text-white truncate" data-testid="hero-track-title">{t.title}</p>
                  {playable ? (
                    <p className="font-mono text-[8px] tracking-[0.3em] text-white/40 mt-1">
                      {["TEASER", "PREVIEW"].includes(t.availability)
                        ? `TEASER${t.teaserDuration || t.previewDuration ? ` · ${t.teaserDuration || t.previewDuration}″` : ""}`
                        : (slide.releaseTitle || "").toUpperCase()}
                    </p>
                  ) : (
                    <p className="font-mono text-[8px] tracking-[0.3em] text-white/35 mt-1" data-testid="hero-track-locked">
                      {t.lockLabel || "SIGNAL NOT YET AVAILABLE"}
                    </p>
                  )}
                </div>
              </div>
              {active && (
                <div className="mt-4">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[9px] text-white/40 w-8">{fmt(progress)}</span>
                    <input type="range" min={0} max={duration || 0} value={progress} step={0.1}
                      onChange={(e) => seek(Number(e.target.value))} aria-label="Seek"
                      data-testid="hero-player-seek"
                      className="flex-1 h-[3px] appearance-none bg-white/15 accent-[#d4af37] cursor-pointer" />
                    <span className="font-mono text-[9px] text-white/40 w-8">{fmt(duration)}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <button onClick={stop} data-testid="hero-player-stop" aria-label="Stop"
                      className="w-8 h-8 rounded-full border border-white/20 text-white/60 flex items-center justify-center hover:border-white/50 hover:text-white transition-colors">
                      <Square size={10} />
                    </button>
                    <button onClick={toggleMute} data-testid="hero-player-mute" aria-label={muted ? "Unmute" : "Mute"} aria-pressed={muted}
                      className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors ${
                        muted ? "border-gold/60 text-gold" : "border-white/20 text-white/60 hover:border-white/50 hover:text-white"}`}>
                      {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
                    </button>
                    {t.slug && (
                      <button onClick={() => navigate(`/track/${t.slug}`)} data-testid="hero-track-page-link"
                        className="ml-auto font-mono text-[8px] tracking-[0.3em] text-white/40 hover:text-gold transition-colors">
                        TRACK PAGE →
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="font-mono text-[9px] tracking-[0.3em] text-white/30" data-testid="hero-no-track">SIGNAL NOT YET AVAILABLE</p>
          )}
        </div>
      )}

      {slide.restricted && (
        <p className="font-mono text-[10px] tracking-[0.3em] text-red-500/70 mt-8" data-testid="hero-dv-status">
          {entitled ? "ACCESS VERIFIED — THE FIFTH DIMENSION IS OPEN TO YOU." : "VERIFIED ACCESS REQUIRED"}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-4 mt-8">
        <button onClick={() => onEnter(slide, ctaPrimary.target)} data-testid="hero-cta-primary"
          className="bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-8 py-4 hover:bg-white transition-colors duration-300">
          {ctaPrimary.label}
        </button>
        {ctaSecondary && (
          <button onClick={() => navigate(ctaSecondary.target)} data-testid="hero-cta-secondary"
            className="border border-white/30 text-white font-mono text-[11px] tracking-[0.25em] px-8 py-4 hover:border-gold hover:text-gold transition-colors duration-300">
            {ctaSecondary.label}
          </button>
        )}
      </div>
    </div>
  );
}

export default function DimensionHero({ slides }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { playing } = usePlayer();
  const [active, setActive] = useState(0);
  const [hovering, setHovering] = useState(false);
  const pauseRef = useRef(0);
  const touchRef = useRef(null);
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const len = slides.length;

  const goTo = useCallback((i, isUser = true) => {
    setActive(((i % len) + len) % len);
    if (isUser) {
      pauseRef.current = Date.now() + 14000;
      const s = slides[((i % len) + len) % len];
      if (s) trackEvent("hero_dimension_select", { dimension: s.number });
    }
  }, [len, slides]);

  useEffect(() => {
    if (reduced || len < 2) return;
    const id = setInterval(() => {
      if (hovering || playing || Date.now() < pauseRef.current) return;
      setActive((a) => (a + 1) % len);
    }, 6000);
    return () => clearInterval(id);
  }, [reduced, hovering, playing, len]);

  if (!len) return null;
  const slide = slides[active];
  const rel = (i) => {
    let d = i - active;
    if (d > len / 2) d -= len;
    if (d < -len / 2) d += len;
    return d;
  };

  const enter = (s, target) => {
    trackEvent("enter_dimension", { dimension: s.number });
    navigate(target || (s.restricted ? "/complete-set" : DIM_ROUTES[s.number] || "/"));
  };

  const onKey = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); goTo(active - 1); }
    if (e.key === "ArrowRight") { e.preventDefault(); goTo(active + 1); }
  };

  return (
    <section
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505] flex items-center"
      data-testid="dimension-hero"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onFocus={() => setHovering(true)}
      onBlur={() => setHovering(false)}
    >
      {/* ambient depth */}
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute inset-0 transition-opacity duration-1000 ${slide.number === "01" ? "opacity-100" : "opacity-0"}`}><div className="di-stars" /></div>
        <div className="absolute -top-1/3 right-[-10%] w-[70vw] h-[70vw] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.07),transparent_60%)]" />
        <div className="mm-vignette" />
      </div>

      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 md:px-12 pt-28 pb-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* LEFT — headline (always first) */}
          <div className="lg:col-span-5 order-1">
            <AnimatePresence mode="wait">
              <motion.div key={slide.number}
                initial={{ opacity: 0, y: reduced ? 0 : 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: reduced ? 0 : -16 }}
                transition={{ duration: reduced ? 0 : 0.6, ease: EASE }}>
                <p className="font-mono text-[10px] md:text-[11px] tracking-[0.55em] text-gold" data-testid="hero-series-label">MULTIDIMENSIONAL</p>
                <h1 className="mt-5 font-serif font-light leading-[0.95]">
                  {slide.restricted ? (
                    <>
                      <span className="block text-4xl sm:text-5xl lg:text-6xl text-white/85">THE FIFTH</span>
                      <span className="block text-4xl sm:text-5xl lg:text-6xl text-white/85">DIMENSION</span>
                    </>
                  ) : (
                    <>
                      <span className="block text-3xl sm:text-4xl text-white/70 tracking-[0.12em]">{slide.title}</span>
                      <span className={`block text-6xl sm:text-7xl lg:text-8xl mt-2 ${slide.number === "01" ? "di-metal" : "text-white"}`}>
                        {slide.identity || slide.theme}
                      </span>
                    </>
                  )}
                </h1>
                <p className="font-mono text-[10px] md:text-xs tracking-[0.4em] text-white/45 mt-5" data-testid="hero-tagline">
                  {slide.restricted ? "ACCESS RESTRICTED — SIGNAL DETECTED" : slide.tagline?.toUpperCase()}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* details on desktop */}
            <div className="hidden lg:block mt-8">
              <AnimatePresence mode="wait">
                <motion.div key={slide.number}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: reduced ? 0 : 0.5, delay: reduced ? 0 : 0.1 }}>
                  <SlideDetails slide={slide} user={user} onEnter={enter} />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* RIGHT — carousel stage */}
          <div className="lg:col-span-7 order-2">
            <div
              role="region" aria-roledescription="carousel" aria-label="Dimension covers"
              tabIndex={0} onKeyDown={onKey}
              data-testid="hero-carousel"
              className="relative h-[52vw] max-h-[340px] md:max-h-[560px] md:h-[560px] outline-none"
              onTouchStart={(e) => { touchRef.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                if (touchRef.current == null) return;
                const dx = e.changedTouches[0].clientX - touchRef.current;
                if (Math.abs(dx) > 45) goTo(active + (dx < 0 ? 1 : -1));
                touchRef.current = null;
              }}
            >
              {slides.map((s, i) => (
                <HeroCard key={s.number} slide={s} off={rel(i)} reduced={reduced}
                  onSelect={() => goTo(i)} onEnter={(sl) => enter(sl)} />
              ))}
            </div>

            {/* controls */}
            <div className="flex items-center justify-center gap-6 mt-6">
              <button onClick={() => goTo(active - 1)} data-testid="hero-prev" aria-label="Previous dimension"
                className="w-10 h-10 rounded-full border border-white/20 text-white/60 flex items-center justify-center hover:border-gold hover:text-gold transition-colors">
                <ChevronLeft size={16} />
              </button>
              <div className="flex items-center gap-2.5" role="tablist" aria-label="Dimensions">
                {slides.map((s, i) => (
                  <button key={s.number} onClick={() => goTo(i)} role="tab" aria-selected={i === active}
                    aria-label={`Go to ${s.title}`} data-testid={`hero-dot-${s.number}`}
                    className={`h-[5px] rounded-full transition-all duration-500 ${
                      i === active ? (s.restricted ? "w-8 bg-red-500/80" : "w-8 bg-gold") : "w-[5px] bg-white/25 hover:bg-white/50"}`} />
                ))}
              </div>
              <button onClick={() => goTo(active + 1)} data-testid="hero-next" aria-label="Next dimension"
                className="w-10 h-10 rounded-full border border-white/20 text-white/60 flex items-center justify-center hover:border-gold hover:text-gold transition-colors">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* details on mobile */}
          <div className="order-3 lg:hidden">
            <SlideDetails slide={slide} user={user} onEnter={enter} />
          </div>
        </div>
      </div>
    </section>
  );
}
